import { cn } from "@src/utils/cn"
import clsx from "clsx"
import Link from "next/link"
import type { ReactNode } from "react"

export type ExploreItem = {
  title: string
  description: string
  href?: string
  onClick?: () => void
  comingSoon?: boolean
  illustration: ReactNode
  illustrationVariant?: "green" | "gray"
}

export const CARD_SURFACE_CLASSES =
  "-outline-offset-1 bg-white text-left outline outline-gray-900/10 dark:bg-gray-800 dark:outline-white/5"
export const INTERACTIVE_CARD_OUTLINE_CLASSES =
  "hover:-outline-offset-2 focus-visible:-outline-offset-2 hover:outline-2 hover:outline-gray-900/50 focus-visible:outline-2 focus-visible:outline-gray-900/50 dark:focus-visible:outline-white/50 dark:hover:outline-white/50"

export const ExploreItem = ({ item }: { item: ExploreItem }) => {
  const {
    title,
    description,
    href,
    illustration,
    illustrationVariant = "gray",
    comingSoon,
    onClick,
  } = item

  const content = (
    <>
      <div
        className={cn(
          "pointer-events-none flex h-24 select-none items-start justify-center overflow-hidden pt-6",
          {
            "bg-green-500 dark:bg-green-950": illustrationVariant === "green",
            "bg-gray-800 dark:bg-gray-900": illustrationVariant === "gray",
          }
        )}
        aria-hidden="true"
      >
        <div className="relative">
          {illustrationVariant === "green" && (
            <div className="absolute inset-0 hidden rounded-t-xl blur-2xl dark:block dark:bg-green-500/10" />
          )}
          <div className="relative">{illustration}</div>
        </div>
      </div>

      <div className="relative flex flex-1 flex-col gap-0.5 p-6">
        <div className="-top-px absolute inset-x-0 h-px bg-gray-200 group-hover:inset-x-0.5 group-focus-visible:inset-x-0.5 dark:bg-white/5" />
        {comingSoon && (
          <div className="-translate-y-1/2 absolute inset-x-0 top-0 flex h-6 items-center justify-start bg-sky-400 px-6 group-hover:inset-x-0.5 group-hover:px-5.5 group-focus-visible:inset-x-0.5 group-focus-visible:px-5.5 dark:bg-sky-700">
            <span className="font-semibold text-white text-xs/4 tracking-tight">
              Coming soon
            </span>
          </div>
        )}
        <h3 className="font-semibold text-base/6 text-gray-900 tracking-tight dark:text-white">
          {title}
        </h3>
        <p className="text-pretty font-medium text-gray-500 text-sm/5 min-[900px]:text-balance dark:text-gray-400">
          {description}
        </p>
      </div>
    </>
  )

  const classes = clsx(
    "group relative flex w-[clamp(260px,80vw,300px)] snap-center flex-col overflow-hidden rounded-2xl min-[900px]:col-span-1 min-[900px]:w-auto min-[900px]:min-w-0",
    CARD_SURFACE_CLASSES,
    (href || onClick) && INTERACTIVE_CARD_OUTLINE_CLASSES
  )

  if (href) {
    return (
      <Link href={href} className={classes}>
        {content}
      </Link>
    )
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    )
  }

  return <div className={classes}>{content}</div>
}
